/**
 * overviewView.ts — pure view-model helpers for Operations / Overview (M6).
 *
 * The mockup's Overview (klaxiora-crm-mockup-full.html :1678-1718) computes
 * everything from its in-memory demo stores (renderOpsOverview :3844-3880,
 * fed by the other screens at :5018-5019, :5109-5113, :5182-5183,
 * :5259-5260). The real page computes CLIENT-SIDE over the five shipped
 * list endpoints — the M2-M5a posture — a STATED DEVIATION from design doc
 * §B.1's server-aggregate line (§B.1 now records this). Ceiling: each list
 * is fetched with limit 200, so every figure here silently truncates past
 * 200 rows per module; if any tenant approaches that, the §B.1 fallback is
 * the /v1/sales/stats-style server aggregate (psql field-diff gate).
 *
 * Formulas that already have tested implementations are COMPOSED, never
 * reimplemented: incidentKpis (incidentsView), assetKpis (assetsView),
 * qcKpis (qualityView), localISODate (assetsView). New logic here covers
 * only the task-derived KPIs, the feed, and the incidents-panel slice.
 *
 * Honesty calls (decisions, M6): "Active agents" is relabeled "Team with
 * tasks" — the real app has no field-agent registry (assignees are
 * employees, decision M0), so the card counts distinct assignees with ≥1
 * task; completion rate is MONTH-SCOPED so its "this month" line is true
 * (the mockup's formula was all-time under a month label, :3849-3851 vs
 * :1696); tasks-today runs against the REAL injected clock (the mockup
 * hardcodes '2026-07-27', :3852); audit pass rate stays all-time exactly
 * like the Quality page's own card (qcKpis.passRate — one formula, two
 * surfaces, no disagreement).
 */
import type { OpsTask, OpsIncident } from '../../types'
import { localISODate } from './assetsView'
import { taskTypeLabel } from './tasksView'
import { formatSopDate } from './sopsView'

// ── Task-derived KPIs ─────────────────────────────────────────────────────────

export interface TaskKpis {
  /** Distinct assignees holding ≥1 task, any status — the honest stand-in
   *  for the mockup's "Active agents" (computeAgentTaskCounts :3779-3789
   *  skips Unassigned; so does this). */
  teamWithTasks: number
  /** Tasks dated today (real clock), any status — mockup parity (:3853
   *  counts completed and scheduled alike). */
  tasksToday: number
  /** Completed / total across tasks SCHEDULED in now's calendar month;
   *  null (render '—') when the month holds no tasks. Month-scoped so the
   *  card's "this month" line is true — the resolvedThisMonth precedent
   *  (incidentsView.ts). */
  completionRate: number | null
  /** Still-scheduled tasks across the whole list (:3850-3851). */
  scheduled: number
}

export function taskKpis(tasks: OpsTask[], now: Date): TaskKpis {
  const today = localISODate(now)
  const monthPrefix = today.slice(0, 7) // "YYYY-MM" — scheduledDate is plain-date wire
  const inMonth = tasks.filter(t => t.scheduledDate.startsWith(monthPrefix))
  const completedInMonth = inMonth.filter(t => t.status === 'completed').length
  return {
    teamWithTasks: new Set(tasks.filter(t => t.assignedTo).map(t => t.assignedTo)).size,
    tasksToday: tasks.filter(t => t.scheduledDate === today).length,
    completionRate: inMonth.length ? Math.round((completedInMonth / inMonth.length) * 100) : null,
    scheduled: tasks.filter(t => t.status === 'scheduled').length,
  }
}

/** The open-incidents card's sub-line — the mockup's rule verbatim
 *  (:5113): severe count wins, then "None urgent", then "Nothing open". */
export function openIncidentsSubline(open: number, severeOpen: number): string {
  if (severeOpen > 0) return `${severeOpen} critical/high`
  return open === 0 ? 'Nothing open' : 'None urgent'
}

// ── Recent activity feed (derived — decision M6, option 1) ────────────────────
//
// Derived from the CURRENT task list, exactly as the mockup derives its feed
// from the store (:3861-3863: sort by date desc, slice 5). These are recent
// items, NOT an audit log: an edited task shows its new state, a deleted
// task vanishes. A durable activity/event table is the M7+ upgrade if the
// feed is ever asked to remember.

export interface ActivityFeedItem {
  id: string
  /** Assignee display name; the honest 'Unassigned' when none. */
  person: string
  /** The mockup's verb pair (:3872). */
  verb: 'completed' | 'has scheduled'
  taskLabel: string
  /** "7 July 2026 · 09:30" — date, plus the time when the task has one
   *  (:3873). */
  timeLine: string
}

export function activityFeed(tasks: OpsTask[], limit = 5): ActivityFeedItem[] {
  return tasks
    .slice()
    .sort((a, b) =>
      b.scheduledDate.localeCompare(a.scheduledDate) ||
      (b.scheduledTime ?? '').localeCompare(a.scheduledTime ?? '') ||
      b.createdAt.localeCompare(a.createdAt))
    .slice(0, limit)
    .map(t => ({
      id: t.id,
      person: t.assignedToName || 'Unassigned',
      verb: t.status === 'completed' ? 'completed' as const : 'has scheduled' as const,
      taskLabel: taskTypeLabel(t.taskType),
      timeLine: `${formatSopDate(t.scheduledDate)}${t.scheduledTime ? ` · ${t.scheduledTime}` : ''}`,
    }))
}

// ── Open-incidents panel slice ────────────────────────────────────────────────

/** Open incidents, newest first, capped at 4 (:5122). The list endpoint
 *  already orders created_at DESC, so filter + slice preserves it. */
export function openIncidentsPanel(incidents: OpsIncident[], limit = 4): OpsIncident[] {
  return incidents.filter(i => i.status === 'open').slice(0, limit)
}
